'use client'

import { Avatar } from '@repo/ui/Avatar'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useEffect, useState } from 'react'
import { useFeeEarnerStore } from '@repo/store/useFeeEarnerStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const FeeEarnerSelect = ({
  testData = null,
  value = null,
  onChange = (val) => {
    console.log(val)
  },
  sx = {},
  required = false,
  clearable = true,
  size = 'small',
  menuZindex = 1100,
  label = 'Fee earner',
  name = 'feeEarner',
  onlyIncludeSupervisingFeeEarners = false,
  errors = {},
  menuWidth = null
}) => {
  const { fetchFeeEarners, feeEarners } = useFeeEarnerStore()
  const { feeEarner, supervisingFeeEarner } = useNewMatterStore()
  const [options, setOptions] = useState(testData || feeEarners || [])
  const [selectedValue, setSelectedValue] = useState(value)

  useEffect(() => {
    if (testData) return

    let items = []

    if (feeEarners?.length) {
      items = onlyIncludeSupervisingFeeEarners
        ? feeEarners.filter((feeEarner) => feeEarner.isSupervisingFeeEarner)
        : feeEarners
    }

    setOptions(items)
  }, [testData, feeEarners, onlyIncludeSupervisingFeeEarners])

  useEffect(() => {
    if (testData) return
    fetchFeeEarners()
  }, [testData, fetchFeeEarners])

  // Effect to map string `value` to corresponding object
  useEffect(() => {
    if (onlyIncludeSupervisingFeeEarners && supervisingFeeEarner) {
      setSelectedValue(supervisingFeeEarner)
    } else if (!onlyIncludeSupervisingFeeEarners && feeEarner) {
      setSelectedValue(feeEarner)
    } else if (typeof value === 'string') {
      const selectedFeeEarner = options.find((feeEarner) => feeEarner.feeEarnerId === value)
      if (selectedFeeEarner) {
        setSelectedValue(selectedFeeEarner)
      }
    } else {
      setSelectedValue(value)
    }
  }, [value, options, feeEarner, supervisingFeeEarner, onlyIncludeSupervisingFeeEarners])

  return (
    <div data-testid="FeeEarnerSelect" style={{ ...sx }}>
      <ReactSelectMenu
        label={label}
        onChange={(newVal) => {
          setSelectedValue(newVal)
          onChange(newVal)
        }}
        items={options}
        value={selectedValue}
        sx={{ minWidth: '200px', width: '100%' }}
        searchable
        valueCustomRenderFunction={renderFeeEarner}
        optionCustomRenderFunction={renderFeeEarner}
        valueKey="feeEarnerId"
        labelKey="description"
        required={required}
        clearable={clearable}
        name={name}
        size={size}
        menuZindex={menuZindex}
        menuWidth={menuWidth}
        errors={errors}
      />
    </div>
  )
}

const renderFeeEarner = ({ value, label }) => {
  if (!value) return null

  return (
    <MUIStack direction="row" alignItems="center" sx={{ cursor: 'pointer', width: '100%' }} gap={2}>
      <Avatar sx={{ width: '24px', height: '24px', fontSize: '0.675rem' }} feeEarnerId={value} name={label} />
      <MUITypography variant="body1" noWrap sx={{ mb: 0, pointerEvents: 'none' }}>
        {label}
      </MUITypography>
    </MUIStack>
  )
}

FeeEarnerSelect.propTypes = {
  testData: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func,
  sx: PropTypes.object,
  required: PropTypes.bool,
  clearable: PropTypes.bool,
  menuZindex: PropTypes.number,
  size: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  onlyIncludeSupervisingFeeEarners: PropTypes.bool,
  errors: PropTypes.object,
  menuWidth: PropTypes.string
}
